import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
  withStyles,
} from '@material-ui/core';
import { SEARCH_MIN_LENGTH } from '../../constants/lms-course-review.constants';
import { normalizeResourceId } from '../../utils/lms-course-review.utils';
import styles from './resource-search-results.style';

const isSelectedOption = (selectedId, option) =>
  normalizeResourceId(selectedId) === normalizeResourceId(option.id ?? option.resourceKey);

const ResourceSearchResults = ({
  classes,
  options,
  loading,
  searchInput,
  selectedResource,
  onSelect,
}) => {
  const trimmed = (searchInput || '').trim();
  const selectedId = selectedResource?.resourceKey ?? selectedResource?.id;

  if (trimmed.length > 0 && trimmed.length < SEARCH_MIN_LENGTH) {
    return (
      <Typography variant="caption" color="textSecondary" data-testid="search-min-length-hint">
        Enter at least {SEARCH_MIN_LENGTH} characters to search.
      </Typography>
    );
  }

  if (loading && options.length === 0) {
    return (
      <Box display="flex" justifyContent="center" padding={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (trimmed.length >= SEARCH_MIN_LENGTH && !loading && options.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary" data-testid="no-search-results">
        No matching courses found.
      </Typography>
    );
  }

  if (options.length === 0) {
    return null;
  }

  return (
    <Box data-testid="resource-search-results">
      {loading ? (
        <Box marginY={1}>
          <LinearProgress data-testid="resource-search-loading" />
        </Box>
      ) : null}
      <List dense>
        {options.map((option) => {
          const optionId = option.id ?? option.resourceKey;
          const selected = isSelectedOption(selectedId, option);
          const externalIdSuffix = option.externalId ? ` · ${option.externalId}` : '';
          return (
            <ListItem
              key={normalizeResourceId(optionId)}
              button
              selected={selected}
              className={selected ? classes.listItemSelected : undefined}
              onClick={() => onSelect(option)}
              data-testid={`resource-result-${optionId}`}
              data-selected={selected ? 'true' : 'false'}
            >
              <ListItemText
                primary={option.name}
                secondary={`Course ID: ${optionId}${externalIdSuffix}`}
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

ResourceSearchResults.propTypes = {
  options: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  searchInput: PropTypes.string,
  selectedResource: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
};

ResourceSearchResults.defaultProps = {
  loading: false,
  searchInput: '',
  selectedResource: null,
};

export default withStyles(styles)(ResourceSearchResults);
